/**
 * Cómo se ve una reserva cuando la mira el panel.
 *
 * Existe por una razón muy concreta: los turnos ahora entran al panel por DOS
 * caminos —el `GET /api/bookings` de siempre y el listener de Firestore que los
 * trae en vivo— y los dos tienen que dejarlos en la MISMA forma. El documento
 * que guarda Firestore no es el que dibuja la pantalla: la reserva tiene
 * `customer: { name, phone, dni… }` y `service: { name, duration… }` anidados, y
 * las tarjetas del panel leen `customerName`, `phone` y un `service` que es
 * texto. Ese remapeo vivía suelto adentro del `fetch` del Dashboard, así que un
 * turno que llegara por el otro camino se dibujaba sin nombre de cliente y con
 * `[object Object]` donde va el servicio.
 *
 * Todo lo de acá es puro: entra un documento, sale un objeto. Sin red, sin
 * estado, sin `Date.now()` escondido —el instante entra por parámetro, igual que
 * en backend/src/recordatorios.js—.
 */

import { aFechaISO } from './calendario';

/** Cuántos días para atrás se muestran. Es la misma ventana que aplica `GET /api/bookings`. */
export const DIAS_HACIA_ATRAS = 30;

/**
 * Un documento de `bookings` con la forma que espera el panel.
 *
 * Defensivo a propósito con `customer` y `service`. El código que reemplaza
 * hacía `booking.customer.name` directo, lo cual está bien cuando los datos
 * vienen filtrados por el backend, pero acá los documentos llegan crudos desde
 * Firestore: alcanza UNA reserva vieja sin `customer` para que reviente el
 * `.map()` entero y el panel se quede sin ningún turno, no sin uno.
 */
export const aTurnoDePanel = (doc = {}) => ({
  id: doc.id,
  time: doc.time,
  date: doc.date,
  customerName: doc.customer?.name,
  phone: doc.customer?.phone || 'No especificado',
  /* Datos de paciente. Sólo los traen los turnos de un negocio de salud que los
     pidió al reservar; en el resto vienen undefined y la tarjeta no muestra
     nada. */
  dni: doc.customer?.dni || null,
  obraSocial: doc.customer?.obraSocial || null,
  numeroDeAfiliado: doc.customer?.numeroDeAfiliado || null,
  service: doc.service?.name,
  status: doc.status || 'pending',
  staff: doc.staff || null,
  assignedStaff: doc.assignedStaff || null,
  resource: doc.resource || null,
  assignedResource: doc.assignedResource || null,
  resourceName: doc.resourceName || null,
  slotIndex: doc.slotIndex || null,
  // Cuándo lo pidieron, que no es lo mismo que para cuándo es.
  createdAt: doc.createdAt || null,
  // Hasta cuándo está apartado el horario mientras se paga la seña. Sólo lo
  // traen los `pending_deposit`; en todo lo demás es null.
  holdExpiresAt: doc.holdExpiresAt || null,
});

/**
 * La fecha más vieja que se muestra, en `YYYY-MM-DD`.
 *
 * Con `aFechaISO` y NO con `toISOString().split('T')[0]`, que es como lo arma
 * hoy el backend: a UTC−3, un `new Date()` de las 21:00 en adelante serializa al
 * día SIGUIENTE, así que de noche la ventana se corría un día y el turno más
 * viejo desaparecía de la lista. Ver la nota sobre esto en CLAUDE.md.
 */
export const desdeCuando = (ahora = Date.now(), dias = DIAS_HACIA_ATRAS) => {
  const limite = new Date(ahora);
  limite.setDate(limite.getDate() - dias);
  return aFechaISO(limite);
};

/**
 * Los turnos dentro de la ventana, ordenados como los muestra el panel: por
 * fecha descendente y, dentro del día, por hora descendente.
 *
 * Se compara texto contra texto (`'2026-08-05' >= '2026-07-06'`), que en formato
 * ISO ordena igual que las fechas y no construye un `Date` por elemento en cada
 * comparación — ni arrastra la zona horaria a una cuenta que es de calendario,
 * no de instantes.
 */
export const ordenarTurnos = (turnos, ahora = Date.now()) => {
  const limite = desdeCuando(ahora);

  return (Array.isArray(turnos) ? turnos : [])
    .filter((t) => t && typeof t.date === 'string' && t.date >= limite)
    .sort(masRecientePrimero);
};

/**
 * El criterio de orden del panel, en un solo lugar: fecha descendente y, dentro
 * del día, hora descendente. Lo usan la lista general y el historial de cada
 * persona, que tienen que leerse igual.
 */
const masRecientePrimero = (a, b) => {
  if (a.date !== b.date) return a.date < b.date ? 1 : -1;
  return enMinutos(b.time) - enMinutos(a.time);
};

/** "09:30" -> 570. Lo que no tenga esa forma va al final del día. */
const enMinutos = (hora) => {
  const [h, m] = String(hora ?? '').split(':').map(Number);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : -1;
};

/**
 * Con qué comparar dos referencias a la misma persona.
 *
 * Hace falta porque una reserva NO guarda el id del profesional: guarda una
 * COPIA entera de la persona tal como estaba el día que se reservó
 * (`staff: { id, name, email, phone, schedule… }`). Tocar el nombre del
 * profesional en un turno abre su ficha, y para eso hay que encontrar a esa
 * persona en el equipo de HOY partiendo de esa copia, que puede haber quedado
 * vieja.
 *
 * Dos trampas reales de esta base:
 *
 *   1. **Los ids no son todos del mismo tipo.** Conviven números
 *      (`1785944385791`, del onboarding, que usa `Date.now()`), números chicos
 *      (`1`, `3`, de negocios viejos) y texto (`'staff-1'`, de la demo). Un
 *      `===` entre `1` y `'1'` da false y la ficha no abriría nunca, así que se
 *      compara siempre como texto.
 *
 *   2. **Puede no haber id.** Algunas reservas viejas traen la copia sin `id`.
 *      Ahí se cae al nombre normalizado, que no es tan bueno —dos personas que
 *      se llaman igual se mezclan— pero es lo único que queda.
 */
export const idDeProfesional = (persona) => {
  const id = persona && typeof persona === 'object' ? persona.id : null;
  return id === undefined || id === null || String(id) === '' ? null : String(id);
};

export const nombreDeProfesional = (persona) => {
  if (!persona) return null;
  const nombre = typeof persona === 'string' ? persona : persona.name;
  const limpio = String(nombre ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
  return limpio || null;
};

/**
 * ¿Estas dos referencias hablan de la misma persona?
 *
 * Por id sólo cuando LOS DOS lo tienen. Si a uno le falta —las reservas viejas
 * guardaron la copia sin `id`— se compara por nombre, que es el único dato que
 * queda en común. Exigir id de los dos lados dejaba el respaldo inservible justo
 * en el caso para el que existe: la ficha salía vacía para todos los turnos
 * anteriores a que las copias empezaran a llevar id.
 *
 * El riesgo asumido es que dos personas que se llaman igual y una sin id se
 * mezclen. Es peor mostrar una ficha vacía: eso se lee como "no tiene turnos",
 * que es una afirmación falsa, y no hay forma de darse cuenta.
 */
export const mismoProfesional = (a, b) => {
  const idA = idDeProfesional(a);
  const idB = idDeProfesional(b);
  if (idA && idB) return idA === idB;

  const nombreA = nombreDeProfesional(a);
  return nombreA !== null && nombreA === nombreDeProfesional(b);
};

/**
 * Los turnos que atiende esta persona.
 *
 * Mismo orden que la lista general del panel —lo más reciente arriba—, y a
 * propósito: es la misma pregunta hecha sobre menos turnos, así que invertirlo
 * acá obligaría a leer dos listas con dos convenciones distintas en la misma
 * pantalla.
 *
 * Ojo con lo que NO puede mostrar: sale de los turnos que el panel tiene
 * cargados, que son los de los últimos 30 días en adelante (ver
 * `DIAS_HACIA_ATRAS`). No es el historial completo de la persona desde que
 * entró; para eso habría que ir a buscarlo al servidor.
 */
export const turnosDelProfesional = (turnos, persona) => {
  if (!persona) return [];
  return (Array.isArray(turnos) ? turnos : [])
    .filter((t) => mismoProfesional(t?.staff, persona))
    .sort(masRecientePrimero);
};

/**
 * Los ids que están en `actual` y no estaban en `conocidos`.
 *
 * Es lo que permite avisar "llegaron 2 turnos nuevos" en vez de meterlos en la
 * lista sin decir nada. Reordenar la pantalla mientras alguien está leyendo un
 * turno es peor que pedirle que refresque: pierde el lugar donde estaba y no
 * entiende por qué se movió todo.
 *
 * Los cambios sobre turnos que YA estaban —una cancelación, una seña que se
 * pagó— no entran acá y se aplican solos: ahí no hay nada que reordenar y
 * enterarse tarde de una cancelación es justamente el problema a resolver.
 */
export const turnosNuevos = (conocidos, actual) => {
  const yaEstaban = conocidos instanceof Set ? conocidos : new Set(conocidos || []);
  return (Array.isArray(actual) ? actual : [])
    .filter((t) => t?.id && !yaEstaban.has(t.id))
    .map((t) => t.id);
};
